import { useEffect, useState } from 'react';
import { useMinicrmMigrationStore } from '../../store/minicrmMigrationStore';
import { SummaryCards } from '../../components/admin/minicrm-migration/SummaryCards';
import { SchoolReviewTable } from '../../components/admin/minicrm-migration/SchoolReviewTable';
import { ContactsTab } from '../../components/admin/minicrm-migration/ContactsTab';

type Tab = 'schools' | 'contacts';

export function MinicrmMigrationPage() {
  const { summary, run, busy, isLoading, error, fetchSummary, fetchSchools, rescan, commit } = useMinicrmMigrationStore();
  const [tab, setTab] = useState<Tab>('schools');

  // Include decided rows so the School Review tab shows the "agree / Change" view.
  useEffect(() => { fetchSummary(); fetchSchools(true); }, [fetchSummary, fetchSchools]);

  if (isLoading && !summary) return <div className="p-4 text-sm text-gray-500">Loading…</div>;
  if (error) return <div className="p-4 text-sm text-red-700">{error}</div>;
  // Empty state: the backend always returns a summary object (zero counts when
  // empty), so gate on total === 0 — `!summary` alone would never fire post-load.
  if (!summary || summary.total === 0) return <div className="p-4 text-sm text-gray-500">No proposals yet — run a scan.<div className="mt-2"><button className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm disabled:opacity-40" onClick={() => rescan()} disabled={busy}>{busy ? 'Scanning…' : 'Re-scan'}</button></div></div>;

  const commitBlocked = summary.schoolsPending > 0 || busy || summary.committed >= summary.total;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">MiniCRM Migration</h1>
        <div className="flex items-center gap-2">
          <button
            className="px-4 py-2 rounded border text-sm disabled:opacity-40"
            onClick={() => rescan()}
            disabled={busy}
          >
            {busy && run?.kind === 'propose' ? 'Scanning…' : 'Re-scan'}
          </button>
          <button
            className="px-4 py-2 rounded bg-blue-600 text-white text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
            onClick={() => commit()}
            disabled={commitBlocked}
            title={summary.schoolsPending > 0 ? `${summary.schoolsPending} schools still pending` : ''}
          >
            {busy && run?.kind === 'commit' ? 'Committing…' : summary.schoolsPending > 0 ? `Commit approved · blocked: ${summary.schoolsPending} schools` : 'Commit approved'}
          </button>
        </div>
      </div>

      {run && !run.running && run.exitCode !== null && run.exitCode !== 0 && (
        <div className="rounded border border-red-200 bg-red-50 text-red-700 text-xs p-3">
          Last {run.kind} run failed (exit {run.exitCode}). {run.tail.slice(-3).join(' · ')}
        </div>
      )}

      <SummaryCards summary={summary} />

      <div className="border-b text-sm">
        {(['schools', 'contacts'] as Tab[]).map((t) => (
          <button
            key={t}
            className={`px-4 py-2 ${tab === t ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
            onClick={() => setTab(t)}
          >
            {t === 'schools' ? `School Review (${summary.schoolsPending})` : 'Contacts'}
          </button>
        ))}
      </div>

      {tab === 'schools' ? <SchoolReviewTable /> : <ContactsTab />}
    </div>
  );
}
